import React from 'react';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';

import { formatPrice } from '../../services/formatPrice';
import useCheckoutState from '../../hooks/useCheckoutState';
import useProration from '../../hooks/useProration';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import StyledElement from '../StyledElement';

// The proration "Renews on / Today" summary. The single-price price block
// (Product.js `Price`) renders this inline for the selected/lone price; on a
// multi-price product the PricePicker renders one per option, passing each
// option's `price` so every card shows its own proration up front (computed on
// load, independent of selection). Returns null unless the target price has a
// billing-cycle anchor — that gating + the per-price amount/date both live in
// useProration.
//
// `elementPathPrefix` selects which element's enabled toggles gate the two
// lines: the single-price block passes the default (product.pricing); the
// PricePicker passes product.pricePicker.option so the picker's own
// proration-renews / proration-amount eye toggles apply.
export default function Proration({ price, elementPathPrefix = 'product.pricing' }) {
  const { checkout, currencyLocale, theme } = useCheckoutState();
  const { proration, hasProration, isLoadingProration } = useProration(price);
  const product = checkout?.product;
  const labels = getThemeLabels(theme);

  // No billing-cycle anchor (or no product) → this price never shows a proration
  // line; collapse entirely so nothing is reserved.
  if (!product || !hasProration) return null;

  // The amount/date load from an async Stripe call. While loading, render the two
  // lines as empty, height-reserving placeholders so the card doesn't jump when
  // the values arrive ("just use empty lines"). On failure (loaded, no data) the
  // !proration check below collapses it.
  if (isLoadingProration) {
    return (
      <div className="cp_product-price-proration-wrapper" aria-hidden="true">
        <div className="cp_product-price-proration-date-wrapper">
          <span
            className={clsx(
              'cp_product-price-proration-renews-wrapper',
              '[font-size:var(--cp_product-price-prorationRenews-font-size)]',
              '[line-height:var(--cp_product-price-prorationRenews-line-height)]',
            )}
          >
            {'\u00A0'}
          </span>
          <span
            className={clsx(
              'cp_product-price-proration-amount-wrapper',
              '[font-size:var(--cp_product-price-prorationAmount-font-size)]',
              '[line-height:var(--cp_product-price-prorationAmount-line-height)]',
            )}
          >
            {'\u00A0'}
          </span>
        </div>
      </div>
    );
  }

  if (!proration) return null;

  return (
    <div className="cp_product-price-proration-wrapper">
      <div className="cp_product-price-proration-date-wrapper">
        <StyledElement
          elementPath={`${elementPathPrefix}.prorationRenews`}
          as="span"
          className={clsx(
            'cp_product-price-proration-renews-wrapper',
            'bg-[var(--cp_product-price-prorationRenews-background-color)]',
            'text-[var(--cp_product-price-prorationRenews-color)]',
            '[font-family:var(--cp_product-price-prorationRenews-font-family)]',
            '[font-style:var(--cp_product-price-prorationRenews-font-style)]',
            '[font-size:var(--cp_product-price-prorationRenews-font-size)]',
            '[line-height:var(--cp_product-price-prorationRenews-line-height)]',
            '[font-weight:var(--cp_product-price-prorationRenews-font-weight)]',
          )}
        >
          <span className="cp_product-price-proration-date-text">{labels.renewsOnText} </span>
          {proration.nextBillingDate && (
            <span className="cp_product-price-proration-date">
              {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
            </span>
          )}
        </StyledElement>

        <StyledElement
          elementPath={`${elementPathPrefix}.prorationAmount`}
          className={clsx(
            'cp_product-price-proration-amount-wrapper',
            'bg-[var(--cp_product-price-prorationAmount-background-color)]',
            'text-[var(--cp_product-price-prorationAmount-color)]',
            '[font-family:var(--cp_product-price-prorationAmount-font-family)]',
            '[font-style:var(--cp_product-price-prorationAmount-font-style)]',
            '[font-size:var(--cp_product-price-prorationAmount-font-size)]',
            '[line-height:var(--cp_product-price-prorationAmount-line-height)]',
            '[font-weight:var(--cp_product-price-prorationAmount-font-weight)]',
          )}
        >
          <span className="cp_product-price-proration-amount-today-text">{labels.todayText} </span>
          <span className="cp_product-price-proration-amount">
            {formatPrice({
              amount: proration.amount,
              // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
              currency: product.currency,
              locale: currencyLocale,
              style: 'currency',
            })}{' '}
          </span>
          {!proration.fullPrice && (
            <span className="cp_product-price-proration-amount-text">
              {labels.proratedAmountText}
            </span>
          )}
        </StyledElement>
      </div>
    </div>
  );
}
